import { api, signIn as storeCredentials } from "@/shared/api";
import { endCoreSession, restoreCoreSession, signedInWithCore } from "./core";
import { forgetTenantId, rememberTenantId } from "./tenant";

/**
 * Bring a live session back after a page reload.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 *
 * The access token lives in memory and nowhere else (doc 23 §11a), which is the
 * right call — a token in `localStorage` is a token any injected script can
 * read and use. The cost was that every reload and every pasted deep link
 * signed the user out, and an SME review recorded that as the highest-friction
 * defect in the product.
 *
 * The fix is not to start storing the token. `POST /auth/refresh` is
 * authenticated by an `HttpOnly`, `SameSite=Strict` cookie holding only the
 * session id: a script cannot read it, another origin cannot cause the browser
 * to send it, and what comes back is a short-lived token that goes straight
 * back into memory.
 *
 * ── (H) A failure here is a normal state, not an error ────────────────────
 *
 * Nobody signed in yet, the session idled out, somebody signed out in another
 * tab — all of them answer `401`, and all of them mean "show the sign-in
 * screen". So this resolves to `false` rather than throwing, and nothing
 * upstream reports it. Treating an ordinary cold start as an error would put a
 * problem notice in front of a person who has done nothing wrong.
 */
export async function restoreSession(tenantId: string): Promise<boolean> {
  if (tenantId === "") return false;
  // A Sarva Core session has no PMS refresh cookie (D315): it is restored by exchanging a
  // fresh Core token for the same tenant.
  if (signedInWithCore()) return restoreCoreSession(tenantId);
  try {
    const envelope = await api.refreshToken({
      headers: { "X-Sarva-Tenant-Id": tenantId },
    });
    const token = envelope.data;
    storeCredentials({
      accessToken: token.access_token,
      expiresAt: token.expires_at,
      actorId: token.actor_id,
      tenantId: token.tenant_id,
      sessionId: token.session_id,
      // (H) Not known on a restore. The token carries no email and `/me` has none
      // either, so the account menu falls back to the actor id rather than showing
      // a name this path cannot know. Inventing one would be worse than omitting it.
      email: null,
    });
    rememberTenantId(token.tenant_id);
    return true;
  } catch {
    return false;
  }
}

/**
 * End the session on the server as well as in this tab.
 *
 * Clearing the client's memory alone would leave a session anybody holding its
 * id could still refresh — the difference between signing out and closing the
 * tab.
 */
export async function endSession(tenantId: string): Promise<void> {
  forgetTenantId();
  if (signedInWithCore()) await endCoreSession();
  try {
    await api.signOut({ headers: { "X-Sarva-Tenant-Id": tenantId } });
  } catch {
    // (H) Swallowed on purpose. The local credential is dropped by the caller either
    // way: a sign-out that failed to reach the server must still look like a sign-out
    // here, or the user stays apparently signed in on a shared machine.
  }
}
